import { BRUSHES, BRUSH_IDS, normalizeBrushSettings, type BrushSettings } from './brushes';
import { paintBrushV2 } from './brush-renderer';
import type { Drawing, Stroke, StrokeBrush, StrokeShape, DrawingEvent } from './types';

export const emptyDrawing = (): Drawing => ({ schemaVersion: 1, strokes: [] });
export const copyDrawing = (drawing: Drawing): Drawing => JSON.parse(JSON.stringify(drawing));
export const duration = (drawing: Drawing) => {
  const points = drawing.strokes[drawing.strokes.length - 1]?.points;
  return points?.[points.length - 1]?.t ?? 0;
};
export const replayEventSpan = (event: DrawingEvent) => {
  if (event.kind !== 'stroke' || !event.stroke) return 280;
  const points = event.stroke.points; const first = points[0]?.t ?? 0; const last = points.at(-1)?.t ?? first;
  return Math.max(300, last - first);
};
/** 回放时长必须与 renderDrawingProgress 使用同一套事件分段，避免自动压缩到 10 秒时提前标记完成。 */
export const replayDuration = (drawing: Drawing) => drawing.events?.length ? Math.max(1, drawing.events.reduce((total, event) => total + replayEventSpan(event), 0)) : Math.max(1, duration(drawing));
/** 油漆桶填过的背景色；没填过时是白纸。 */
export const drawingBackground = (drawing: Drawing) => drawing.background ?? '#FFFFFF';
/** 笔头类型：v0.0.100 之前的作品没有 brush 字段，一律当钢笔。 */
export const strokeBrush = (stroke: Stroke): StrokeBrush => stroke.brush ?? 'pen';
/** 橡皮擦回背景色：填过背景的画纸上擦除不会留下白斑。 */
export const strokeColor = (stroke: Stroke, background: string) => stroke.tool === 'eraser' ? background : stroke.color;
/** 半透明笔头的不透明度；其他笔头恒为 1。 */
export const strokeOpacity = (stroke: Stroke) => stroke.tool === 'eraser' ? 1 : Math.max(0.05, Math.min(1, stroke.opacity ?? 1));
/** 镂空笔头的内芯色；老作品没有该字段时按白纸处理。 */
export const strokeInner = (stroke: Stroke) => stroke.inner ?? '#FFFFFF';
/** 镂空内芯粗细：内芯与笔迹等比缩小，任何粗细下都留得下那圈颜色。 */
export const outlineInnerWidth = (width: number) => Math.max(1, width * 0.62);
/** 换笔头时用的默认粗细。 */
export const STROKE_DEFAULT_WIDTH = Object.fromEntries(BRUSHES.map(b => [b.id, b.defaults.width ?? 12])) as Record<StrokeBrush, number>;
/** 半透明类笔头的默认不透明度。 */
export const STROKE_DEFAULT_OPACITY: Partial<Record<StrokeBrush, number>> = { translucent: 0.5, marker: 0.35, spray: 0.45, glow: 0.9 };
/** 形状笔头画实心时用的不透明度（低于它就画实心，否则只描边）。 */
export const SHAPE_FILL_OPACITY = 0.45;
/** 只在真的填过色时写出 background 字段，保持老作品的 JSON 形状不变。 */
export const setDrawingBackground = (drawing: Drawing, background?: string) => {
  if (background) drawing.background = background; else delete drawing.background;
  return drawing;
};
/** 蜡笔抖动的幅度比例（相对笔迹粗细）。 */
const CRAYON_AMPLITUDE = 0.22;
/** 喷枪：每个采样点喷出的颗粒数与最大颗粒半径（相对笔迹粗细）。 */
const SPRAY_DOTS = 14;
const SPRAY_DOT_RADIUS = 0.11;
/**
 * 手指采样点之间可能隔了几十逻辑像素，只按采样点喷会画成一串独立的团。
 * 沿着线段按固定间距补插喷点（间距随粗细缩放），颗粒才连成一条喷雾。
 */
const SPRAY_SPACING_RATIO = 0.16;
const SPRAY_SPACING_MIN = 5;
const SPRAY_SPACING_MAX = 16;
/** 毛笔：慢笔最粗到基准粗细的 1.35 倍，快笔最细到 0.5 倍。 */
const INK_SLOWEST = 1.35;
const INK_FASTEST = 0.5;
/** 光晕：外圈 / 中圈 / 芯共三层，粗细倍数与各自的不透明度。 */
const GLOW_RINGS = [{ scale: 2.1, alpha: 0.16 }, { scale: 1.5, alpha: 0.3 }, { scale: 1, alpha: 1 }];
/** 形状笔头缺省画圆。 */
export const strokeShape = (stroke: Stroke): StrokeShape => stroke.shape ?? 'circle';
/** 虚线的线段与间隔长度。 */
export const strokeDash = (stroke: Stroke) => {
  const segment = Math.max(1, stroke.width * 1.6); const gap = Math.max(1, stroke.width * 1.2);
  const dash = stroke.dash;
  return dash && dash.length >= 2 && dash.every(value => value >= 0) ? dash : [segment, gap];
};

/**
 * 确定性伪随机：同一（点序 / 颗粒序）永远得到同一个值。
 * 蜡笔与喷枪的颗粒感必须可复现，否则编辑器、回放、缩略图、导出与运营审核台会画出四张不同的画。
 */
function hashUnit(index: number, salt: number) {
  const value = Math.sin((index + 1) * 127.1 + salt * 311.7) * 43758.5453123;
  return value - Math.floor(value);
}
const jitterAt = (index: number, salt: number) => (hashUnit(index, salt) - 0.5) * 2;
/** 笔画身份：只用起点，双方渲染时数据完全一致，镜像笔画也有不同取值。 */
const strokeSeed = (stroke: Stroke) => Math.round(stroke.points[0].x * 0.37 + stroke.points[0].y * 0.91 + stroke.width);

type Texture = { points: { x: number; y: number }[]; cap: CanvasLineCap };
/** 笔头的绘制纹理：几何折线（蜡笔带确定性抖动）与线帽样式。 */
function strokeTexture(stroke: Stroke): Texture {
  const brush = strokeBrush(stroke);
  const cap: CanvasLineCap = brush === 'marker' ? 'butt' : 'round';
  if (brush !== 'crayon') return { points: stroke.points, cap };
  const amplitude = Math.max(0.8, stroke.width * CRAYON_AMPLITUDE);
  // 只抖位置、不抖存在：每个采样点都要落下，否则回放最后一帧会少一段笔迹。
  const points = stroke.points.map((point, index) => {
    const offset = { x: jitterAt(index, 0) * amplitude, y: jitterAt(index, 97) * amplitude };
    if (index === 0) return { x: point.x + offset.x, y: point.y + offset.y };
    const before = stroke.points[index - 1];
    return { x: (before.x + point.x) / 2 + offset.x, y: (before.y + point.y) / 2 + offset.y };
  });
  return { points, cap };
}

function tracePath(ctx: CanvasRenderingContext2D, texture: Texture) {
  const points = texture.points;
  ctx.beginPath(); ctx.moveTo(points[0].x, points[0].y);
  for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
  // 单点笔画（点一下就抬手）用圆头线帽画不出来，补一个圆点。
  if (points.length === 1) ctx.arc(points[0].x, points[0].y, Math.max(0.5, ctx.lineWidth / 2), 0, Math.PI * 2);
}

/**
 * 形状笔头：两点定形——起点与终点是包围盒的对角，圆心取包围盒中心。
 * 圆按包围盒的短边做正圆，避免手指拖出椭圆；三角形是等腰三角。
 */
function traceShape(ctx: CanvasRenderingContext2D, stroke: Stroke) {
  const [from, to] = stroke.points;
  const left = Math.min(from.x, to.x); const right = Math.max(from.x, to.x);
  const top = Math.min(from.y, to.y); const bottom = Math.max(from.y, to.y);
  const cx = (left + right) / 2; const cy = (top + bottom) / 2;
  const half = Math.max(1, Math.min(right - left, bottom - top) / 2);
  const shape = strokeShape(stroke);
  ctx.beginPath();
  if (shape === 'rect') ctx.rect(left, top, Math.max(1, right - left), Math.max(1, bottom - top));
  else if (shape === 'triangle') { ctx.moveTo(cx, cy - half); ctx.lineTo(cx + half, cy + half); ctx.lineTo(cx - half, cy + half); ctx.closePath(); }
  else ctx.arc(cx, cy, half, 0, Math.PI * 2);
}

/** 毛笔：按运笔速度（两点间距）插值笔宽——慢慢画更粗，快笔收细。 */
function inkWidth(stroke: Stroke, index: number) {
  const points = stroke.points;
  const before = points[Math.max(0, index - 1)]; const after = points[Math.min(points.length - 1, index + 1)];
  const distance = Math.hypot(after.x - before.x, after.y - before.y);
  // 采样点间距 < 6 视为慢笔，> 26 视为快笔。
  const ratio = Math.max(0, Math.min(1, (distance - 6) / 20));
  return stroke.width * (INK_SLOWEST + (INK_FASTEST - INK_SLOWEST) * ratio);
}

/** 喷枪：以采样点为中心确定性撒点，中间按固定间距补插；数据量仍是普通笔画，颗粒在渲染时才算。 */
function paintSpray(ctx: CanvasRenderingContext2D, stroke: Stroke, color: string) {
  const seed = strokeSeed(stroke);
  const radius = Math.max(1.2, stroke.width / 2);
  const dotRadius = Math.max(0.35, stroke.width * SPRAY_DOT_RADIUS);
  const spacing = Math.max(SPRAY_SPACING_MIN, Math.min(SPRAY_SPACING_MAX, stroke.width * SPRAY_SPACING_RATIO));
  ctx.fillStyle = color;
  let index = 0;
  const burst = (x: number, y: number) => {
    for (let dot = 0; dot < SPRAY_DOTS; dot++) {
      const angle = hashUnit(index * SPRAY_DOTS + dot, seed) * Math.PI * 2;
      // 开方让颗粒在圆面上均匀分布，而不是挤在圆心。
      const distance = Math.sqrt(hashUnit(index * SPRAY_DOTS + dot, seed + 61)) * radius;
      ctx.beginPath();
      ctx.arc(x + Math.cos(angle) * distance, y + Math.sin(angle) * distance, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }
    index++;
  };
  for (let i = 0; i < stroke.points.length; i++) {
    const point = stroke.points[i];
    burst(point.x, point.y);
    const next = stroke.points[i + 1];
    if (!next) break;
    const steps = Math.min(40, Math.floor(Math.hypot(next.x - point.x, next.y - point.y) / spacing));
    for (let step = 1; step < steps; step++) {
      const ratio = step / steps;
      burst(point.x + (next.x - point.x) * ratio, point.y + (next.y - point.y) * ratio);
    }
  }
}

/** 光晕：同一条路径叠三层（外圈淡、中圈柔、芯亮），在深色背景上像霓虹灯。 */
function paintGlow(ctx: CanvasRenderingContext2D, stroke: Stroke, color: string) {
  const texture = strokeTexture(stroke);
  ctx.lineCap = texture.cap; ctx.lineJoin = 'round'; ctx.strokeStyle = color;
  for (const ring of GLOW_RINGS) {
    ctx.globalAlpha = ring.alpha;
    ctx.lineWidth = Math.max(1, stroke.width * ring.scale);
    tracePath(ctx, texture);
    if (texture.points.length === 1) ctx.fill(); else ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** 毛笔：逐段用各自的线宽画，圆头线帽保证接头看不出接缝。 */
function paintInk(ctx: CanvasRenderingContext2D, stroke: Stroke, color: string) {
  const texture = strokeTexture(stroke);
  const points = texture.points;
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = color; ctx.fillStyle = color;
  if (points.length === 1) { ctx.lineWidth = stroke.width * INK_SLOWEST; tracePath(ctx, texture); ctx.fill(); return; }
  for (let index = 1; index < points.length; index++) {
    ctx.lineWidth = inkWidth(stroke, index);
    ctx.beginPath();
    ctx.moveTo(points[index - 1].x, points[index - 1].y);
    ctx.lineTo(points[index].x, points[index].y);
    ctx.stroke();
  }
}

function paintStroke(ctx: CanvasRenderingContext2D, stroke: Stroke, background: string) {
  if (stroke.brushVersion === 2) { paintBrushV2(ctx, stroke, background, createLayerCanvas); return; }
  const brush = strokeBrush(stroke);
  const color = strokeColor(stroke, background);
  ctx.save();
  ctx.setLineDash([]);
  if (brush === 'spray' && stroke.tool !== 'eraser') paintSpray(ctx, stroke, color);
  else if (brush === 'glow' && stroke.tool !== 'eraser') paintGlow(ctx, stroke, color);
  else if (brush === 'ink' && stroke.tool !== 'eraser') paintInk(ctx, stroke, color);
  else if (brush === 'shape') {
    // 实心 = 用较低的不透明度填满；空心只描边（形状笔头默认空心，描边宽度即当前粗细）。
    const filled = strokeOpacity(stroke) < 0.6;
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = stroke.width;
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    traceShape(ctx, stroke);
    if (filled) ctx.fill(); else ctx.stroke();
  } else {
    const texture = strokeTexture(stroke);
    ctx.lineCap = texture.cap; ctx.lineJoin = 'round';
    if (brush === 'dash') ctx.setLineDash(strokeDash(stroke));
    for (const pass of strokePasses(stroke, background)) {
      ctx.strokeStyle = ctx.fillStyle = pass.color; ctx.lineWidth = pass.width;
      tracePath(ctx, texture);
      // 单点笔画只需 fill（tracePath 已经给出圆形路径），折线只需 stroke。
      if (texture.points.length === 1) ctx.fill(); else ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * 逐段描边会把半透明笔画在线段接头处叠深，蜡笔与喷枪的颗粒、光晕的多层、毛笔的变宽也不允许逐段重画；
 * 这类笔头必须先整条画到透明层、再一次性合成到画面（同时也保证笔迹之间有正确的先后层级）。
 */
const LAYERED: StrokeBrush[] = ['crayon', 'spray', 'glow', 'ink'];
function needsLayer(stroke: Stroke) { return stroke.brushVersion === 2 || strokeOpacity(stroke) < 1 || LAYERED.includes(strokeBrush(stroke)); }

/**
 * 透明层画布池：半透明笔画必须一笔一张（同一条笔画自己不能叠深），
 * 编辑器每帧、回放每帧都会用到，不能每次都新建 1000×1000。
 * 池子里的第 0 张就是脚本里回收出来的空画布，取用即从池中移除。
 */
const layerPool = new Map<number, HTMLCanvasElement[]>();
let canvasFactory: ((width: number, height: number) => HTMLCanvasElement) | null = null;
/**
 * 注册平台建画布的方法（客户端在构造时把 platform.createCanvas 送进来）。
 * **不能只靠 document.createElement**：微信小游戏里没有 document，那样取不到透明层，
 * 半透明笔画会退化成实心，并且把 globalAlpha 遗留给后面所有笔画（v0.0.101 真机「钢笔盖不住半透明、橡皮擦不掉」的根因）。
 */
export function setStrokeCanvasFactory(factory: (width: number, height: number) => HTMLCanvasElement) { canvasFactory = factory; }
/** 清掉平台工厂与透明层缓存（测试用：模拟「平台给不出离屏画布」的降级路径）。 */
export function resetStrokeCanvasFactory() { canvasFactory = null; layerPool.clear(); }
function createLayerCanvas(size: number) {
  if (canvasFactory) return canvasFactory(size, size);
  const dom = (globalThis as { document?: { createElement(tag: string): HTMLCanvasElement } }).document;
  if (!dom) return undefined;
  const canvas = dom.createElement('canvas');
  canvas.width = size; canvas.height = size;
  return canvas;
}
function takeLayer(size: number) {
  const reused = layerPool.get(size)?.pop();
  return reused ?? createLayerCanvas(size);
}
/** 清空并回收：脚本没有回收入口，把空画布放回池子，最多留 4 张。 */
function hangUpLayer(size: number, canvas: HTMLCanvasElement) {
  const lc = canvas.getContext('2d');
  if (!lc) return;
  lc.setTransform(1, 0, 0, 1, 0, 0); lc.globalAlpha = 1; lc.clearRect(0, 0, size, size);
  const free = layerPool.get(size) ?? [];
  if (free.length < 4) { free.push(canvas); layerPool.set(size, free); }
}

/**
 * 把一条需要混合的笔画画到自己的透明层上，再整层贴回画面。
 * 一笔一层是「后画的笔迹永远盖在前面的之上」的保证；透明层画布从池里取，用完立刻清空回收。
 * 万一平台连一张透明层都给不出来，宁可画成实心（不透明度失效），也**绝不能**把 globalAlpha
 * 留在主画布上——那会让后面每一笔都变成半透明，笔迹互相盖不住、橡皮也擦不干净。
 */
function compositeStroke(ctx: CanvasRenderingContext2D, size: number, draw: (context: CanvasRenderingContext2D) => void, alpha = 1, fallback = draw) {
  const layer = takeLayer(size);
  try {
    if (!layer) { fallback(ctx); return; }
    const lc = layer.getContext('2d')!;
    lc.setTransform(1, 0, 0, 1, 0, 0); lc.globalAlpha = 1; lc.clearRect(0, 0, size, size);
    lc.save(); lc.scale(size / 1000, size / 1000); lc.lineCap = 'round'; lc.lineJoin = 'round';
    draw(lc); lc.restore();
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = alpha; ctx.drawImage(layer, 0, 0, size, size);
    ctx.restore();
  } finally {
    ctx.globalAlpha = 1;
    if (layer) hangUpLayer(size, layer);
  }
}

/** 按时间裁点：一条笔画从头重画，只画已经出现的部分。 */
function clipStroke(stroke: Stroke, until: number): Stroke | null {
  const points = until === Infinity ? stroke.points : stroke.points.filter(point => point.t <= until);
  if (!points.length) return null;
  return points.length === stroke.points.length ? stroke : { ...stroke, points };
}

/** 一条笔画怎么上色：镂空是「粗的当前色 + 细的内芯色」两遍，其余一笔画完。 */
function strokePasses(stroke: Stroke, background: string) {
  const outer = { color: strokeColor(stroke, background), width: stroke.width };
  if (strokeBrush(stroke) !== 'outline' || stroke.tool === 'eraser') return [outer];
  return [outer, { color: strokeInner(stroke), width: outlineInnerWidth(stroke.width) }];
}

/**
 * 一批笔画按**文档顺序**逐条上色：不需要混合的直接画，需要混合的（半透明 / 荧光笔 / 蜡笔）
 * 每条单独走一张透明层再贴回。顺序本身就是正确性来源——后画的笔迹（包括橡皮）永远盖在前面之上，
 * 把半透明笔画攒到最后统一合成会让它们反过来盖住后面画的钢笔与橡皮。
 */
function paintStrokes(ctx: CanvasRenderingContext2D, size: number, background: string, strokes: Stroke[]) {
  for (const stroke of strokes) {
    if (!needsLayer(stroke)) { paintStroke(ctx, stroke, background); continue; }
    compositeStroke(ctx, size, context => {
      context.globalAlpha = stroke.brushVersion === 2 ? 1 : strokeOpacity(stroke);
      paintStroke(context, stroke, background);
    }, stroke.brushVersion === 2 ? strokeOpacity(stroke) : 1,
      stroke.brushVersion === 2 ? context => paintStroke(context,{...stroke,brushVersion:undefined,brush:'pen',opacity:1},background) : undefined);
  }
}

/** 在已有画面上合成单笔，用于只含顺序笔迹的教学回放缓存；当前未完成笔必须每帧从干净底图重画。 */
export function renderDrawingStroke(ctx: CanvasRenderingContext2D, stroke: Stroke, size: number, background: string, until = Infinity) {
  const clipped = clipStroke(stroke, until); if (!clipped) return;
  ctx.save(); ctx.scale(size / 1000, size / 1000);
  paintStrokes(ctx, size, background, [clipped]); ctx.restore();
}

// The same renderer draws the editor, replay, gallery, and operator review.
export function renderDrawing(ctx: CanvasRenderingContext2D, drawing: Drawing, size: number, until = Infinity) {
  const background = drawingBackground(drawing);
  ctx.save();
  ctx.fillStyle = background; ctx.fillRect(0, 0, size, size);
  ctx.scale(size / 1000, size / 1000);
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const visible: Stroke[] = [];
  for (const stroke of drawing.strokes) {
    if (stroke.points[0].t > until) break;
    const clipped = clipStroke(stroke, until);
    if (clipped) visible.push(clipped);
  }
  paintStrokes(ctx, size, background, visible);
  ctx.restore();
}

/** Sprite surfaces have no paper. Erasing removes alpha, without changing legacy drawing semantics. */
export function renderTransparentDrawing(ctx: CanvasRenderingContext2D, drawing: Drawing, size: number) {
  ctx.save(); ctx.clearRect(0, 0, size, size); ctx.scale(size / 1000, size / 1000);
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  for (const stroke of drawing.strokes) {
    ctx.save();
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      paintStroke(ctx, { ...stroke, brushVersion: undefined, brush: 'pen', opacity: 1, color: '#000000' }, '#000000');
    } else paintStrokes(ctx, size, '#FFFFFF', [stroke]);
    ctx.restore();
  }
  ctx.restore();
}

/** 渐进回放：优先消费事件时间线，覆盖清空 / 填充后又重画的完整过程；旧作品回退到最终笔迹。 */
export function renderDrawingProgress(ctx: CanvasRenderingContext2D, drawing: Drawing, size: number, progress = Infinity) {
  if (!drawing.events?.length) { renderDrawing(ctx, drawing, size, progress); return; }
  const background = '#FFFFFF';
  const strokes: Stroke[] = [];
  let paper = background; let elapsed = 0; let painted = false;
  ctx.save(); ctx.scale(size / 1000, size / 1000); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  /**
   * 每帧整批重画而不是逐笔叠加：半透明笔头的结果依赖「直接绘制 / 透明层」的批次先后，
   * 逐笔 merge 会在两种批次交错时画出与 renderDrawing 不一致的画面。
   * 纸面底色必须用恒等变换铺满，否则会被逻辑坐标的缩放重复放大。
   */
  const paint = () => {
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = paper; ctx.fillRect(0, 0, size, size);
    ctx.restore();
    paintStrokes(ctx, size, paper, strokes);
    painted = true;
  };
  for (const event of drawing.events) {
    const start = elapsed; const span = replayEventSpan(event);
    if (progress < start) break;
    const local = Math.min(span, progress - start);
    if (event.kind === 'clear') { strokes.length = 0; paper = background; paint(); }
    else if (event.kind === 'fill') { paper = event.background ?? background; paint(); }
    else if (event.stroke) {
      // 正在画的这一笔按 local 裁点（单点笔画至少留一个点，回放里不会出现空笔画）；
      // 其余笔画已经在 strokes 里，整批重画自动处理批次先后。
      strokes.push(clipStroke(event.stroke, (event.stroke.points[0]?.t ?? 0) + local)!);
      if (event.mirrorStroke) strokes.push(clipStroke(event.mirrorStroke, (event.mirrorStroke.points[0]?.t ?? 0) + local)!);
      paint();
    }
    elapsed += span; if (progress < elapsed) break;
  }
  // 已经画过的那一帧就是最终状态：再画一遍等于把半透明笔迹叠两次。
  if (!painted) paint();
  ctx.restore();
}

type EditAction =
  | { kind: 'stroke'; stroke: Stroke; mirrorStroke?: Stroke; event?: DrawingEvent }
  // clear / fill 都记录“操作前的整张纸”和“操作后的背景色”：撤销回到前者，重做回到后者。
  | { kind: 'clear'; strokes: Stroke[]; previousBackground?: string; background?: string; event?: DrawingEvent }
  | { kind: 'fill'; strokes: Stroke[]; previousBackground?: string; background?: string; event?: DrawingEvent };

export interface StrokeOptions {
  brushVersion?: 2; settings?: BrushSettings; seed?: number; shapeFilled?: boolean;
  /** 笔头类型；橡皮仍然是 tool:'eraser' 的独立开关，不受笔头影响。 */
  brush?: StrokeBrush;
  color: string; width: number;
  /** 对称笔：每落一笔同时在画布竖直中线的另一侧镜像一笔。 */
  mirror?: boolean;
  opacity?: number; inner?: string;
  /** 虚线的线段 / 间隔长度；缺省按粗细推导。 */
  dash?: number[];
  /** 形状笔头画什么；缺省圆。 */
  shape?: StrokeShape;
  tool?: Stroke['tool'];
}

export class DrawingEditor {
  document: Drawing;
  revision = 0;
  private undoStack: EditAction[] = [];
  private redoStack: EditAction[] = [];
  private pointCount = 0;
  private active: Stroke | null = null;
  private activeMirror: Stroke | null = null;
  private activeHistory?: { redo: EditAction[]; shifted?: EditAction; clock: number };
  private started = 0;
  private offset = 0;
  private eventClock = 0;
  private preview: { stroke: Stroke; mirror?: Stroke } | null = null;
  constructor(document: Drawing = emptyDrawing()) {
    this.document = copyDrawing(document);
    for (const stroke of this.document.strokes) for (const point of stroke.points) point.t = Math.round(point.t);
    this.undoStack = this.document.strokes.map(stroke => ({ kind: 'stroke', stroke }));
    this.document.events = this.document.events ? [...this.document.events] : this.document.strokes.map(stroke => ({ kind: 'stroke' as const, t: stroke.points[0]?.t ?? 0, stroke }));
    this.eventClock = this.document.events.at(-1)?.t ?? 0;
    this.countPoints();
  }
  private countPoints() { this.pointCount = this.document.strokes.reduce((total, stroke) => total + stroke.points.length, 0); }
  private build(x: number, y: number, options: StrokeOptions, time: number): Stroke {
    const brush: StrokeBrush = options.tool === 'eraser' ? 'pen' : options.brush ?? 'pen';
    const tool: Stroke['tool'] = options.tool ?? (brush === 'line' ? 'line' : brush === 'shape' ? 'shape' : 'pen');
    const stroke: Stroke = { tool, color: options.color, width: options.width, points: [{ x, y, t: time }] };
    if (brush !== 'pen') stroke.brush = brush;
    const opacity = options.opacity ?? STROKE_DEFAULT_OPACITY[brush];
    if (opacity !== undefined && opacity < 1) stroke.opacity = opacity;
    if (brush === 'outline' && options.tool !== 'eraser') stroke.inner = options.inner ?? '#FFFFFF';
    // 虚线把线段 / 间隔随笔画存下来：粗细改了之后回放仍然与当时一致。
    if (brush === 'dash') stroke.dash = options.dash ?? [Math.max(1, options.width * 1.6), Math.max(1, options.width * 1.2)];
    if (brush === 'shape') stroke.shape = options.shape ?? 'circle';
    if (options.brushVersion === 2) {
      stroke.brushVersion = 2; stroke.settings = normalizeBrushSettings(brush, options.settings);
      stroke.seed = options.seed ?? ((Math.round(x * 13 + y * 37 + time * 17)) >>> 0);
      stroke.opacity = options.tool === 'eraser' ? 1 : stroke.settings.opacity / 100;
      if (brush === 'shape') { stroke.shape = options.shape ?? stroke.settings.shape; stroke.shapeFilled = options.shapeFilled ?? stroke.settings.shapeFilled; }
    }
    return stroke;
  }
  private mirrorOf(stroke: Stroke): Stroke {
    return { ...stroke, points: stroke.points.map(point => ({ ...point, x: 1000 - point.x })) };
  }
  start(x: number, y: number, options: StrokeOptions, now: number) {
    this.end();
    if (this.document.strokes.length >= 1500 || this.pointCount >= 40_000) throw new Error('画纸已经很丰富了，请先完成这幅画。');
    this.activeHistory = { redo: this.redoStack, clock: this.eventClock };
    this.started = now; this.offset = duration(this.document) + 100;
    this.active = this.build(x, y, options, this.offset);
    this.activeMirror = options.mirror && options.tool !== 'eraser' ? this.mirrorOf(this.active) : null;
    this.document.strokes.push(this.active);
    if (this.activeMirror) this.document.strokes.push(this.activeMirror);
    this.eventClock += 1;
    const event: DrawingEvent = { kind: 'stroke', t: this.eventClock, stroke: this.active, ...(this.activeMirror ? { mirrorStroke: this.activeMirror } : {}) };
    this.document.events!.push(event);
    this.undoStack.push({ kind: 'stroke', stroke: this.active, ...(this.activeMirror ? { mirrorStroke: this.activeMirror } : {}), event });
    if (this.undoStack.length > 1500) this.activeHistory.shifted = this.undoStack.shift();
    this.redoStack = []; this.pointCount += 1 + (this.activeMirror ? 1 : 0); this.revision++;
  }
  move(x: number, y: number, now: number) {
    const target = this.active; if (!target) return;
    const last = target.points[target.points.length - 1];
    if (target.brushVersion === 2 && target.tool !== 'line' && target.tool !== 'shape') { const k = 1 - (target.settings?.stabilize ?? 0) / 125; x = last.x + (x - last.x) * k; y = last.y + (y - last.y) * k; }
    if (Math.hypot(last.x - x, last.y - y) < 2) return;
    if (target.points.length >= 1500 || this.pointCount >= 40_000) return;
    // Compress long pauses but preserve stroke order and all sampled endpoints.
    const t = Math.max(last.t + 1, Math.min(this.offset + Math.round(now - this.started), last.t + 50));
    const point = { x: Math.max(0, Math.min(1000, x)), y: Math.max(0, Math.min(1000, y)), t };
    target.points.push(point);
    this.activeMirror?.points.push({ ...point, x: 1000 - point.x });
    this.pointCount += 1 + (this.activeMirror ? 1 : 0); this.revision++;
  }
  end() { this.active = null; this.activeMirror = null; this.activeHistory = undefined; }
  get activeStrokeCount() { return (this.active ? 1 : 0) + (this.activeMirror ? 1 : 0); }
  /** A second finger cancels only the pending stroke, preserving prior undo/redo. */
  cancelStroke() {
    if (!this.active || !this.activeHistory) return;
    const history = this.activeHistory; const action = this.undoStack.pop();
    this.document.strokes.splice(this.document.strokes.length - (this.activeMirror ? 2 : 1));
    if (action?.event) { const i = this.document.events!.indexOf(action.event); if (i >= 0) this.document.events!.splice(i, 1); }
    if (history.shifted) this.undoStack.unshift(history.shifted);
    this.redoStack = history.redo; this.eventClock = history.clock;
    this.end(); this.countPoints(); this.revision++;
  }
  /**
   * 直线笔头：拖动时只更新预览、抬手才落笔。
   * 预览不进 document、不进撤销栈，中途取消（双指缩放 / 切后台）不会留下任何东西。
   */
  previewLine(from: { x: number; y: number }, to: { x: number; y: number }, options: StrokeOptions) {
    const stroke = this.build(from.x, from.y, { ...options, brush: options.brush === 'shape' ? 'shape' : 'line' }, this.offset);
    stroke.points.push({ x: Math.max(0, Math.min(1000, to.x)), y: Math.max(0, Math.min(1000, to.y)), t: this.offset + 1 });
    this.preview = { stroke, ...(options.mirror ? { mirror: this.mirrorOf(stroke) } : {}) };
    this.revision++;
  }
  get previewStrokes(): Stroke[] { return this.preview ? [this.preview.stroke, ...(this.preview.mirror ? [this.preview.mirror] : [])] : []; }
  clearPreview() { if (!this.preview) return; this.preview = null; this.revision++; }
  undo() {
    this.end(); this.clearPreview(); const action = this.undoStack.pop();
    if (!action) return;
    if (action.kind === 'stroke') {
      this.document.strokes.pop(); if (action.mirrorStroke) this.document.strokes.pop();
      const events = this.document.events ?? []; const i = action.event ? events.indexOf(action.event) : events.findIndex(e => e.stroke === action.stroke); if (i >= 0) events.splice(i, 1);
    }
    else { this.document.strokes = action.strokes; setDrawingBackground(this.document, action.previousBackground); const events = this.document.events ?? []; if (action.event) { const i = events.indexOf(action.event); if (i >= 0) events.splice(i, 1); } }
    this.redoStack.push(action); this.countPoints(); this.revision++;
  }
  redo() {
    this.end(); this.clearPreview(); const action = this.redoStack.pop(); if (!action) return;
    if (action.kind === 'stroke') {
      this.document.strokes.push(action.stroke);
      if (action.mirrorStroke) this.document.strokes.push(action.mirrorStroke);
      const event = action.event ?? { kind: 'stroke' as const, t: ++this.eventClock, stroke: action.stroke, ...(action.mirrorStroke ? { mirrorStroke: action.mirrorStroke } : {}) };
      (this.document.events ??= []).push(event);
    }
    else if (action.kind === 'clear') { this.document.strokes = []; setDrawingBackground(this.document, action.background); const event = action.event ?? { kind: 'clear' as const, t: ++this.eventClock }; (this.document.events ??= []).push(event); }
    else { this.document.strokes = action.strokes; setDrawingBackground(this.document, action.background); const event = action.event ?? { kind: 'fill' as const, t: ++this.eventClock, background: action.background }; (this.document.events ??= []).push(event); }
    this.undoStack.push(action); this.countPoints(); this.revision++;
  }
  /** 油漆桶：把整张画布换成背景色，保留已有笔迹，撤销与重做都能取回对应的背景。 */
  fill(background: string) {
    this.end(); this.clearPreview();
    // strokes 必须浅拷一份：撤销一笔是 pop()、clear() 又会整数组替换，
    // 存活引用会让这一步保存的数组和当前数组脱钩（clear 之后再撤销就会复活已清掉的笔画）。
    this.undoStack.push({ kind: 'fill', strokes: [...this.document.strokes], previousBackground: this.document.background, background });
    if (this.undoStack.length > 1500) this.undoStack.shift();
    const events = [...(this.document.events ?? [])];
    this.document = setDrawingBackground({ schemaVersion: 1, strokes: [...this.document.strokes], events }, background);
    this.eventClock += 1; const event: DrawingEvent = { kind: 'fill', t: this.eventClock, background }; this.document.events!.push(event); const last = this.undoStack.at(-1); if (last && last.kind === 'fill') last.event = event;
    this.redoStack = []; this.revision++;
  }
  clear() {
    this.end(); this.clearPreview(); if (!this.document.strokes.length && !this.document.background) return;
    this.undoStack.push({ kind: 'clear', strokes: [...this.document.strokes], previousBackground: this.document.background });
    const events = [...(this.document.events ?? [])];
    this.document = emptyDrawing(); this.eventClock += 1; const event: DrawingEvent = { kind: 'clear', t: this.eventClock }; this.document.events = [...events, event]; const last = this.undoStack.at(-1); if (last && last.kind === 'clear') last.event = event; this.redoStack = []; this.pointCount = 0; this.revision++;
  }
  snapshot() { return copyDrawing(this.document); }
  get canUndo() { return this.undoStack.length > 0; }
  get canRedo() { return this.redoStack.length > 0; }
}

/** 供界面与测试使用的笔头目录顺序。 */
export const STROKE_BRUSHES: StrokeBrush[] = BRUSH_IDS;
export const STROKE_BRUSH_NAMES = Object.fromEntries(BRUSHES.map(b => [b.id, b.name])) as Record<StrokeBrush, string>;
/** 需要「拖出两点定形」的笔头（与直线共用同一套预览 / 抬手落笔交互）。 */
export const DRAG_BRUSHES: StrokeBrush[] = ['line', 'shape'];
/** 形状笔头的三种形状与显示名。 */
export const STROKE_SHAPES: StrokeShape[] = ['circle', 'rect', 'triangle'];
export const STROKE_SHAPE_NAMES: Record<StrokeShape, string> = { circle: '圆', rect: '方', triangle: '三角' };
